import React, { useState, useEffect, useRef, useContext } from 'react';
import './MainHeader.css';
import logo from '../../office-logo.svg';
import { useAuth } from '../../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, IconButton, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AppsIcon from '@mui/icons-material/Apps';
import MenuIcon from '@mui/icons-material/Menu';
import LoginPopover from '../auth/LoginPopover';
import { EmployeeDocumentService } from '../../services/employee-document.service';
import { EmployeeDocumentType } from '../../interfaces/employee-document.interface';
import { NavDrawerContext } from '../../context/NavDrawerContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import NotificationBell from '../notifications/NotificationBell'; // W3-S
import { displayPersonName } from '../../utils/userUtils';
import DashboardStyleSwitch from '../dashboard/DashboardStyleSwitch';
import FeatureSearch from './FeatureSearch';

const MainHeader = () => {
    const { user, logout, openInstitutionSwitcher } = useAuth();
    const navigate = useNavigate();
    // ---- P1-B portal: the customer portal has its own layout ----
    const onPortal = useLocation().pathname.startsWith('/portal/');
    // ---- end P1-B ----
    // Go to /login explicitly (as the sidebar and mobile drawer do). Letting the route guard bounce
    // the now-logged-out page to /login records it as a deep link, and the NEXT person to log in on
    // this browser would be sent to the previous user's page instead of their own dashboard.
    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };
    const [loginAnchorEl, setLoginAnchorEl] = useState<HTMLElement | null>(null);
    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
    const profileBlobUrlRef = useRef<string | null>(null);
    const isMobile = useIsMobile();
    const { openDrawer } = useContext(NavDrawerContext);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        EmployeeDocumentService.getMyDocuments()
            .then(docs => {
                const pic = docs.find(d => d.documentType === EmployeeDocumentType.PASSPORT_SIZE_PICTURE);
                if (!pic || cancelled) return;
                return EmployeeDocumentService.getDocumentBlobUrl(pic.id);
            })
            .then(url => {
                if (!url || cancelled) return;
                profileBlobUrlRef.current = url;
                setProfileImageUrl(url);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            if (profileBlobUrlRef.current) window.URL.revokeObjectURL(profileBlobUrlRef.current);
        };
    }, [user]);

    if (onPortal) return null; // P1-B (after all hooks)

    return (
        <header className="main-header">
            <div className="logo-container">
                {user && isMobile && (
                    <IconButton
                        onClick={openDrawer}
                        aria-label="Open navigation menu"
                        edge="start"
                        sx={{ mr: 0.5, color: 'text.secondary', width: 44, height: 44 }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}
                {user && (
                    <Tooltip title="All modules">
                        <IconButton
                            component={Link}
                            to="/modules"
                            aria-label="All modules"
                            size="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                        >
                            <AppsIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <img src={logo} alt="Logo" className="logo-image" />
            </div>

            {user && !isMobile && <FeatureSearch />}

            {user ? (
                <div className="profile-section">
                    <DashboardStyleSwitch />
                    {/* W3-S */}
                    <NotificationBell />
                    <Box component="span" className="username-display" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {displayPersonName(user.name, user.username)}
                    </Box>
                    <Link to="/profile" className="profile-link">
                        <Avatar
                            src={profileImageUrl ?? undefined}
                            sx={{ width: 28, height: 28 }}
                        >
                            {!profileImageUrl && <PersonIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>My Profile</Box>
                    </Link>
                    {openInstitutionSwitcher && (user.institutionIds?.length ?? 0) > 1 && (
                        <button onClick={openInstitutionSwitcher} className="logout-button" aria-label="Switch institution">
                            Switch institution
                        </button>
                    )}
                    <button onClick={handleLogout} className="logout-button">
                        Logout
                    </button>
                </div>
            ) : (
                <div className="profile-section">
                    <Button
                        variant="contained"
                        onClick={(e) => setLoginAnchorEl(e.currentTarget)}
                        sx={{
                            backgroundColor: '#007bff',
                            '&:hover': { backgroundColor: '#0056b3' },
                            textTransform: 'none',
                        }}
                    >
                        Login
                    </Button>
                    <LoginPopover
                        anchorEl={loginAnchorEl}
                        open={Boolean(loginAnchorEl)}
                        onClose={() => setLoginAnchorEl(null)}
                    />
                </div>
            )}
        </header>
    );
};

export default MainHeader;
